<template>
  <VContainer
    v-if="display"
    v-on-click-outside="close"
    class="background-secondary"
    data-testid="pwa-prompt"
  >
    <VRow no-gutters align="center">
      <VCol cols="auto" class="pr-4">
        <SBtn icon variant="text" size="small" :aria-label="t('dialog.close.aria-label')" data-testid="pwa-close" @click="close">
          <VIcon aria-hidden="true">
            {{ icons.mdiClose }}
          </VIcon>
        </SBtn>
      </VCol>
      <VCol :cols="platform === 'android' ? 8 : 10" sm="auto">
        <div v-if="platform === 'ios'">
          {{ t('pwa.install.ios1') }} <img class="apple-share" src="~/assets/images/apple-share.svg" alt="">
          {{ t('pwa.install.ios2') }}
        </div>
        <div v-if="platform === 'android'">
          {{ t('pwa.install.android') }}
        </div>
      </VCol>
      <VCol v-if="platform === 'android'" class="text-end">
        <SBtn icon variant="text" :aria-label="t('pwa.install.android')" data-testid="install-android" @click="showAndroidPrompt">
          <VIcon aria-hidden="true">
            {{ icons.mdiDownload }}
          </VIcon>
        </SBtn>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script lang="ts" setup>
// Add-to-home-screen prompt, ported from nuxt2 components/pwa/PwaPrompt.vue.
// Android: the browser's beforeinstallprompt is deferred and replayed from the
// download icon. iOS has no such event, so the instructions show at most once
// every 14 days (pwaPrompt cookie). The installed app starts at ?start=a2hs,
// which marks the session as the app (pwa cookie) instead. AB#64459
import { mdiClose, mdiDownload } from '@mdi/js'
import { vOnClickOutside } from '@vueuse/components'
import { usePersistentStore } from '~/stores/persistent'

const icons = { mdiClose, mdiDownload }
const maxExpiration = new Date('2038-01-19 04:14:07')
const fourteenDays = 12096e5

const { t } = useI18n()
const route = useRoute()
const persistentStore = usePersistentStore()

const display = ref(false)
// One value, not two flags: a device is one platform or neither.
const platform = ref<'ios' | 'android' | null>(null)
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)

// Can run before the platform is known (see onMounted): the offer is kept, and
// shown once the device turns out to be Android.
const onBeforeInstallPrompt = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  deferredPrompt.value = event as BeforeInstallPromptEvent
  display.value = platform.value === 'android'
}

const stopListening = () => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  deferredPrompt.value = null
}

// The installed app starts at ?start=a2hs (or already carries the pwa cookie):
// mark the session as the app instead of prompting.
const markInstalledAppSession = () => {
  persistentStore.updateValue('pwa', true, maxExpiration)
}

const maybeShowIosPrompt = () => {
  if (!persistentStore.persistentState.pwaPrompt) {
    persistentStore.updateValue('pwaPrompt', true, new Date(Date.now() + fourteenDays))
    display.value = true
  }
}

const handleIosStart = () => {
  if (route.query.start !== 'a2hs' && !persistentStore.persistentState.pwa) {
    maybeShowIosPrompt()
  } else {
    markInstalledAppSession()
  }
}

// mobile-detect (~39KB) is only needed in the browser, once: loaded here rather
// than imported, so it stays out of the entry chunk every page downloads.
let unmounted = false
onMounted(async () => {
  // Listening starts before the chunk is fetched: Chrome fires beforeinstallprompt
  // once per page load and does not replay it, so an offer made while the chunk
  // downloads would be lost. Deferring it on a device that turns out not to be
  // Android is harmless: only mobile Chrome shows an install bar of its own.
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  // A failed chunk only costs this prompt, so no error page or report for it.
  const mobileDetect = await import('mobile-detect').catch(() => null)
  // The homepage can be left before the chunk arrives; the unmount hook has
  // already removed the listener then.
  if (unmounted) {
    return
  }
  const md = mobileDetect ? new mobileDetect.default(window.navigator.userAgent) : null
  if (md?.mobile() && md.os() === 'AndroidOS') {
    platform.value = 'android'
    display.value = deferredPrompt.value !== null
    return
  }
  stopListening()
  if (md?.mobile() && md.os() === 'iOS') {
    platform.value = 'ios'
    handleIosStart()
  }
})

// nuxt2 never removed the listener; it is removed here when the homepage unmounts.
onBeforeUnmount(() => {
  unmounted = true
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
})

const showAndroidPrompt = () => {
  if (deferredPrompt.value === null) {
    return
  }
  deferredPrompt.value.prompt()
  deferredPrompt.value.userChoice.then(() => {
    deferredPrompt.value = null
  })
  display.value = false
}

const close = () => {
  display.value = false
}
</script>

<style lang="scss" scoped>
.apple-share {
  width: 18px;
  height: 18px;
  padding-top: 4px;
  margin-bottom: -3px;
}
</style>
